import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict/index.js';
import en from 'date-fns/locale/en-US/index.js';
import jp from 'date-fns/locale/ja/index.js';
import kr from 'date-fns/locale/ko/index.js';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import { UtilConstantS } from '../../../../modules/Util/S/Constant/module/UtilConstantS';
import differenceInHours from 'date-fns/differenceInHours/index.js';
import format from 'date-fns/format/index.js';
import { differenceInCalendarWeeks, getMonth, getYear, isDate, startOfMonth, sub } from 'date-fns';

const locale = ET.lang == 'kr' ? kr : ET.lang == 'jp' ? jp : en;
const suffix = ET.lang == 'kr' ? ' 전' : ET.lang == 'jp' ? '前' : ' ago';

export function ago(date: Date): string {
  return `${formatDistanceToNowStrict(date, { locale })}${suffix}`;
}

export function dateFromNow(date: string | Date): string {
  return ago(new Date(date));
}

export function getUTCToZoneTime(date: string | Date, country: string = UtilConstantS.TZ_SEOUL): Date {
  return utcToZonedTime(date, country);
}

export function formatTimeElapsed(date: string | Date, thresholdHours = 168): string {
  /* 현재 시간과 받은 시간의 차이가 thresholdHours 넘어가면 기본 날짜 표기 */
  if (differenceInHours(new Date(), new Date(date)) > thresholdHours) {
    return format(new Date(date), 'yyyy.MM.dd');
  }

  /* thresholdHours를 넘어가지 않으면 ~ 분 전으로 표기 */
  return dateFromNow(date);
}

/**
 * Date를 "yy년 M월" 형태로 변환
 */
export function formatMonth(date: Date, lang = ET.lang): string {
  const year = getYear(date) % 100;
  const month = getMonth(date) + 1;

  switch (lang) {
    case 'en':
      return `'${year} ${month} month`;
    case 'jp':
      return `${year}年 ${month}月`;
    case 'kr':
    default:
      return `${year}년 ${month}월`;
  }
}

/**
 * Date를 "yy년 M월 N주차" 형태로 변환
 */
export function formatMonthWeek(date: Date, lang = ET.lang): string {
  const year = getYear(date) % 100;
  const month = getMonth(date) + 1;
  const firstDayOfMonth = startOfMonth(date);
  const weekOfMonth = differenceInCalendarWeeks(date, firstDayOfMonth, { weekStartsOn: 0 }) + 1;

  switch (lang) {
    case 'en':
      return `'${year} ${month} month, week ${weekOfMonth}`;
    case 'jp':
      return `${year}年 ${month}月 第${weekOfMonth}週`;
    case 'kr':
    default:
      return `${year}년 ${month}월 ${weekOfMonth}주차`;
  }
}

/**
 * 날짜 초기값 구하는 함수(지난 365일)
 */
export const getStartAndEndDate = (
  start_date_str: string | undefined,
  end_date_str: string | undefined,
  default_duration: 365 | 28 = 365,
): { start_date: Date; end_date: Date } => {
  const _end_date = end_date_str ? new Date(end_date_str) : new Date();
  const end_date = isDate(_end_date) ? _end_date : new Date();

  const _start_date = start_date_str ? new Date(start_date_str) : null;

  // start_date가 유효하면 그대로 사용, 아니면 end_date에서 1년 전 날짜 계산
  const start_date =
    _start_date && isDate(_start_date) ? _start_date : sub(end_date, { days: default_duration });

  return {
    start_date,
    end_date,
  };
};
